import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Breadcrumbs,
  Link,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Divider,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  CircularProgress,
  Autocomplete,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Business as OperationsIcon,
  Inventory as ContainerIcon,
  Search as SearchIcon,
  ArrowForward as ArrowForwardIcon,
  FilterList as FilterListIcon,
} from '@mui/icons-material';
import { useNavigationCore } from '../../../../core/router/navigation';
import { useCompanyCore } from '../../../../core/modules/company';
import { useContainerCore } from '../../../../core/modules/container';
import { toast } from 'react-toastify';
import { 
  ReceptionContainerSearchDialog, 
  ReceptionContainerInfo, 
  ProductSearchDialog,
  LocationSearchDialog,
  LocationInfo,
  ProductSelection
} from '../../../components/operations/containerClassification';
import { ReceptionContainer } from '../../../../core/interfaces/receptionCotnainer.interface';
import { Location } from '../../../../core/interfaces/location.interface';
import { useLocationCore } from '../../../../core/modules/location';
import { useReceptionCore } from '../../../../core/modules/reception';
import { useMasterCore } from '../../../../core/modules/master';

export const ValorativeClassificationScreen: React.FC = () => {
  const { goToWelcome } = useNavigationCore();
  const { companyId } = useCompanyCore();
  const { postProductToLocation } = useContainerCore();
  const { locations, getLocations } = useLocationCore();
  const { getReceptionContainer } = useReceptionCore();
  const {
    productUsages,
    productPackagings,
    productFuntionalities,
    productAesthetics,
    productCompletenesses,
    productStates,
  } = useMasterCore();

  // Estado para los contenedores seleccionados
  const [originContainer, setOriginContainer] = useState<ReceptionContainer | null>(null);
  const [destinationLocation, setDestinationLocation] = useState<Location | null>(null);
  
  // Estado para el producto seleccionado
  const [productClassificationData, setProductClassificationData] = useState<any>(null);
  const [productResetKey, setProductResetKey] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Estado para los diálogos de búsqueda
  const [originSearchOpen, setOriginSearchOpen] = useState(false);
  const [locationSearchOpen, setLocationSearchOpen] = useState(false);
  const [originSearchInput, setOriginSearchInput] = useState('');
  const [locationSearchInput, setLocationSearchInput] = useState('');
  
  // Estado para el paso actual
  const [activeStep, setActiveStep] = useState(0);
  const originRef = useRef<HTMLDivElement | null>(null);
  const classificationRef = useRef<HTMLDivElement | null>(null);
  const destinationRef = useRef<HTMLDivElement | null>(null);

  const steps = [
    { label: 'Origen', description: 'Selecciona el contenedor de origen (recepción)' },
    { label: 'Producto y Clasificación', description: 'Selecciona producto y configura clasificación' },
    { label: 'Destino', description: 'Selecciona la ubicación de destino y confirma' },
  ];

  useEffect(() => {
    if (companyId) {
      getLocations(companyId);
    }
  }, [companyId, getLocations]);

  // Búsqueda automática por código (origen)
  useEffect(() => {
    if (!companyId) return;
    const value = originSearchInput.trim();
    if (!value) {
      setOriginContainer(null);
      return;
    }
    const timeoutId = setTimeout(() => {
      getReceptionContainer(companyId, value, (container) => {
        if (container) {
          setOriginContainer(container);
        } else {
          setOriginContainer(null);
        }
      });
    }, 400);
    return () => clearTimeout(timeoutId);
  }, [originSearchInput, companyId, getReceptionContainer]);

  // Búsqueda automática por código (ubicación) - Removida porque ahora usamos Autocomplete
  // El Autocomplete maneja la selección automática cuando hay coincidencia exacta

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleReset = () => {
    setActiveStep(0);
    setOriginContainer(null);
    setOriginSearchInput('');
    setDestinationLocation(null);
    setLocationSearchInput('');
    setProductClassificationData(null);
    setProductResetKey((prev) => prev + 1);
  };

  const resetAfterClassification = () => {
    if (originContainer) {
      setActiveStep(1);
      setOriginSearchInput(originContainer.code || '');
    } else {
      setActiveStep(0);
      setOriginSearchInput('');
    }
    setDestinationLocation(null);
    setLocationSearchInput('');
    setProductClassificationData(null);
    setProductResetKey((prev) => prev + 1);
  };

  const handleProductClassificationSubmit = useCallback((data: any | null) => {
    setProductClassificationData(data);
  }, []);

  const handleClassificationSubmit = async () => {
    if (!originContainer || !destinationLocation || !productClassificationData) {
      console.error('Faltan datos para la clasificación');
      return;
    }

    setIsSubmitting(true);

    try {
      // Preparar los datos para el POST usando la interfaz existente
      const classificationPayload = {
        product_id: productClassificationData.product_id,
        width_cm: productClassificationData.width_cm,
        length_cm: productClassificationData.length_cm,
        depth_cm: productClassificationData.depth_cm,
        volumetric_weight_kg: productClassificationData.volumetric_weight_kg,
        weight_kg: productClassificationData.weight_kg,
        usage_note: productClassificationData.usage_note,
        packaging_note: productClassificationData.packaging_note,
        functionality_note: productClassificationData.functionality_note,
        aesthetic_note: productClassificationData.aesthetic_note,
        completeness_note: productClassificationData.completeness_note,
        observation: productClassificationData.observation,
        reference: productClassificationData.reference,
        quantity: productClassificationData.quantity,
        set_quantity: 0,
        is_set: false,
        usage_id: productClassificationData.usage_id,
        packaging_id: productClassificationData.packaging_id,
        functionality_id: productClassificationData.functionality_id,
        aesthetic_id: productClassificationData.aesthetic_id,
        completeness_id: productClassificationData.completeness_id,
        state_id: productClassificationData.state_id,
        set_complete: false,
        origin_container_id: originContainer.id
      };

      // Usar el servicio existente postProductToLocation
      await postProductToLocation(companyId!, destinationLocation.id, classificationPayload);
      
      toast.success('Clasificación completada exitosamente');
      resetAfterClassification();
    } catch (error) {
      console.error('Error al enviar la clasificación:', error);
      toast.error('Error al enviar la clasificación. Revisa la consola para más detalles.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOriginContainerSelect = (container: ReceptionContainer) => {
    setOriginContainer(container);
    setOriginSearchInput(container.code || '');
    setOriginSearchOpen(false);
  };

  const handleDestinationLocationSelect = (location: Location) => {
    setDestinationLocation(location);
    setLocationSearchInput(location.code || '');
    setLocationSearchOpen(false);
  };

  const canProceedToNextStep = () => {
    return !!originContainer;
  };

  const canProceedToClassification = () => {
    if (!productClassificationData) return false;
    
    // Verificar que los campos requeridos estén completos
    const requiredFields = [
      'usage_id', 'packaging_id', 'functionality_id', 
      'aesthetic_id', 'completeness_id', 'state_id'
    ];
    
    const allRequiredFieldsFilled = requiredFields.every(field => 
      productClassificationData[field] && productClassificationData[field] > 0
    );
    
    if (!allRequiredFieldsFilled) return false;
    
    // Verificar que la cantidad sea mayor a 0
    if (!productClassificationData.quantity || productClassificationData.quantity <= 0) {
      return false;
    }
    
    // Verificar que las observaciones requeridas estén completas
    const attributeMappings = [
      { id: productClassificationData.usage_id, note: productClassificationData.usage_note, options: productUsages },
      { id: productClassificationData.packaging_id, note: productClassificationData.packaging_note, options: productPackagings },
      { id: productClassificationData.functionality_id, note: productClassificationData.functionality_note, options: productFuntionalities },
      { id: productClassificationData.aesthetic_id, note: productClassificationData.aesthetic_note, options: productAesthetics },
      { id: productClassificationData.completeness_id, note: productClassificationData.completeness_note, options: productCompletenesses },
      { id: productClassificationData.state_id, note: productClassificationData.observation, options: productStates },
    ];
    
    // Verificar que si un atributo requiere observación, la observación esté completa
    for (const mapping of attributeMappings) {
      if (mapping.id) {
        const selectedAttribute = mapping.options?.find(opt => opt.id === mapping.id);
        if (selectedAttribute?.has_description) {
          if (!mapping.note || mapping.note.trim() === '') {
            return false;
          }
        }
      }
    }
    
    return true;
  };

  const isStepCompleted = (index: number) => {
    if (index === 0) return !!originContainer;
    if (index === 1) return canProceedToClassification();
    if (index === 2) return !!destinationLocation;
    return false;
  };
  const workflowReady = isStepCompleted(0) && isStepCompleted(1) && isStepCompleted(2);

  const scrollToRef = (ref: React.RefObject<HTMLDivElement>) => {
    requestAnimationFrame(() => {
      if (ref.current) {
        ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  };

  const canNavigateTo = (index: number) => {
    for (let i = 0; i < index; i += 1) {
      if (!isStepCompleted(i)) return false;
    }
    return true;
  };

  const goToStep = (index: number) => {
    if (!canNavigateTo(index)) return;
    setActiveStep(index);
  };

  useEffect(() => {
    if (activeStep === 0) scrollToRef(originRef);
    if (activeStep === 1) scrollToRef(classificationRef);
    if (activeStep === 2) scrollToRef(destinationRef);
  }, [activeStep]);

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>

      {/* Header sticky con stepper */}
      <Paper sx={{ p: 3, mb: 3, position: 'sticky', top: 0, zIndex: 1000, backdropFilter: 'saturate(180%) blur(6px)', backgroundColor: 'rgba(255,255,255,0.9)', borderBottom: '1px solid', borderColor: 'divider' }}>
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <ContainerIcon color="primary" sx={{ fontSize: 32 }} />
          <Typography variant="h4" component="h1">
            Clasificación a Ubicación
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Ingresar artículos a una ubicación
        </Typography>
        <Box mt={2}>
          <Stepper activeStep={activeStep} orientation="horizontal">
            {steps.map((step, index) => (
              <Step key={step.label} completed={isStepCompleted(index)}>
                <StepLabel
                  onClick={() => goToStep(index)}
                  sx={{ cursor: canNavigateTo(index) ? 'pointer' : 'default' }}
                >
                  <Box display="flex" flexDirection="column" alignItems="center">
                    <Typography variant="body2" fontWeight="medium">{step.label}</Typography>
                    <Typography variant="caption" color="text.secondary">{step.description}</Typography>
                  </Box>
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>
      </Paper>

      {/* Contenido principal */}
      <Box display="flex" flexDirection="column" gap={3}>
        {/* Selección de origen y destino */}
        <Paper sx={{ p: 3, scrollMarginTop: 120 }} ref={originRef}>
          <Typography variant="h6" color="primary" gutterBottom>
            Selección de contenedor de origen
          </Typography>
          
          <Grid container spacing={3}>
            {/* Contenedor de origen */}
            <Grid item xs={12} md={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    Contenedor de origen (Recepción)
                  </Typography>
                  {!originContainer && (
                    <Box className="flex flex-1 w-full sm:w-auto items-center gap-2">
                      <TextField
                        placeholder="Escanear o buscar por código"
                        variant="outlined"
                        size="small"
                        fullWidth
                        value={originSearchInput}
                        onChange={(e) => setOriginSearchInput(e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon />
                            </InputAdornment>
                          ),
                        }}
                      />
                      <IconButton
                        onClick={() => setOriginSearchOpen(true)}
                        size="small"
                        sx={{ 
                          border: '1px solid #ccc',
                          borderRadius: 1,
                          backgroundColor: 'white',
                          '&:hover': { backgroundColor: '#f5f5f5' }
                        }}
                      >
                        <FilterListIcon />
                      </IconButton>
                    </Box>
                  )}
                  {originContainer && (
                    <ReceptionContainerInfo 
                      container={originContainer} 
                      onClear={() => { setOriginContainer(null); setOriginSearchInput(''); }}
                    />
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Botón Continuar al paso de Producto y Clasificación */}
          <Box display="flex" justifyContent="flex-end" mt={2}>
            <Button
              variant="contained"
              onClick={() => setActiveStep(1)}
              disabled={!originContainer}
              endIcon={<ArrowForwardIcon />}
            >
              Continuar
            </Button>
          </Box>
        </Paper>

        {/* Separador visual */}
        <Divider />

        {/* Paso 2: Producto y Clasificación */}
        <Paper sx={{ p: 3, scrollMarginTop: 120 }} ref={classificationRef}>
          <Box sx={{ display: activeStep >= 1 ? 'block' : 'none' }}>
            <Typography variant="h6" gutterBottom>
              Producto y Clasificación
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Selecciona un producto y configura su clasificación para continuar.
            </Typography>

            <ProductSelection
              onProductSelect={handleProductClassificationSubmit}
              companyId={companyId || undefined}
              resetKey={productResetKey}
            />

            <Box display="flex" justifyContent="space-between" mt={3}>
              <Button onClick={() => setActiveStep(0)}>Atrás</Button>
              <Button
                variant="contained"
                onClick={() => setActiveStep(2)}
                disabled={!canProceedToClassification()}
                endIcon={<ArrowForwardIcon />}
              >
                Continuar
              </Button>
            </Box>
          </Box>
        </Paper>

        {/* Paso 3: Destino (Ubicación) */}
        <Paper sx={{ p: 3, scrollMarginTop: 120 }} ref={destinationRef}>
          <Box sx={{ display: activeStep >= 2 ? 'block' : 'none' }}>
            <Typography variant="h6" gutterBottom>
              Destino
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Selecciona la ubicación de destino y confirma la clasificación.
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} md={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom>
                      Ubicación de destino
                    </Typography>
                    {!destinationLocation && (
                      <Box className="flex flex-1 w-full sm:w-auto items-center gap-2">
                        <Autocomplete
                          fullWidth
                          size="small"
                          options={locations || []}
                          getOptionLabel={(option) => option.code || ''}
                          value={locations?.find(loc => loc.code?.toLowerCase() === locationSearchInput.toLowerCase()) || null}
                          inputValue={locationSearchInput}
                          onInputChange={(event, newInputValue) => {
                            setLocationSearchInput(newInputValue);
                            // Si hay una coincidencia exacta, seleccionarla automáticamente
                            const exactMatch = locations?.find(loc => loc.code?.toLowerCase() === newInputValue.toLowerCase());
                            if (exactMatch) {
                              handleDestinationLocationSelect(exactMatch);
                            } else {
                              setDestinationLocation(null);
                            }
                          }}
                          onChange={(event, newValue) => {
                            if (newValue) {
                              handleDestinationLocationSelect(newValue);
                            } else {
                              setDestinationLocation(null);
                              setLocationSearchInput('');
                            }
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="Escanear o buscar por código de ubicación"
                              variant="outlined"
                              InputProps={{
                                ...params.InputProps,
                                startAdornment: (
                                  <>
                                    <InputAdornment position="start">
                                      <SearchIcon />
                                    </InputAdornment>
                                    {params.InputProps.startAdornment}
                                  </>
                                ),
                              }}
                            />
                          )}
                          renderOption={(props, option) => (
                            <Box component="li" {...props} key={option.id}>
                              <Box>
                                <Typography variant="body2" fontWeight="medium">
                                  {option.code}
                                </Typography>
                                {option.description && (
                                  <Typography variant="caption" color="text.secondary">
                                    {option.description}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          )}
                          noOptionsText="No se encontraron ubicaciones"
                        />
                        <IconButton
                          onClick={() => setLocationSearchOpen(true)}
                          size="small"
                          sx={{ 
                            border: '1px solid #ccc',
                            borderRadius: 1,
                            backgroundColor: 'white',
                            '&:hover': { backgroundColor: '#f5f5f5' }
                          }}
                        >
                          <FilterListIcon />
                        </IconButton>
                      </Box>
                    )}
                    {destinationLocation && (
                      <LocationInfo location={destinationLocation} onClear={() => { setDestinationLocation(null); setLocationSearchInput(''); }} />
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Box display="flex" justifyContent="flex-start" mt={3}>
              <Button onClick={() => setActiveStep(1)}>Atrás</Button>
            </Box>
          </Box>
        </Paper>
      </Box>

      {/* Botón flotante Clasificar */}
      <Box sx={{ position: 'fixed', bottom: 16, right: 24, zIndex: 1300 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleClassificationSubmit}
          disabled={!workflowReady || isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
        >
          {isSubmitting ? 'Clasificando...' : 'Clasificar'}
        </Button>
      </Box>

      {/* Diálogos de búsqueda */}
      <ReceptionContainerSearchDialog
        open={originSearchOpen}
        onClose={() => setOriginSearchOpen(false)}
        onSelect={handleOriginContainerSelect}
        title="Buscar contenedor de origen (Recepción)"
        companyId={companyId || undefined}
      />

      <LocationSearchDialog
        open={locationSearchOpen}
        onClose={() => setLocationSearchOpen(false)}
        onSelect={handleDestinationLocationSelect}
        title="Buscar ubicación de destino"
        companyId={companyId || undefined}
      />
    </Container>
  );
};

export default ValorativeClassificationScreen;